import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";

function AttendanceChart({ selectedYear }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  function getCurrentAcademicYear() {
    const today = new Date();
    let startYear = today.getFullYear();
    const month = today.getMonth() + 1;
    if (month < 6) startYear -= 1;
    const shortStart = startYear.toString().slice(-2);
    const shortEnd = (startYear + 1).toString().slice(-2);
    return `${shortStart}-${shortEnd}`;
  }

  const safeYear = selectedYear || getCurrentAcademicYear();

  const yearForRequest = safeYear.includes("-")
    ? safeYear.replace("-", "-20")
    : safeYear;

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetch(
      `https://aadyaacademy.org/api/dashboard/attendanceBreakdown?year=${yearForRequest}`
    )
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch attendance data");
        return res.json();
      })
      .then((data) => {
        console.log("Attendance API response:", data);
        if (Array.isArray(data.breakdown)) {
          setSections(data.breakdown);
        } else {
          setSections([]);
          setError("Unexpected data format from API");
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Something went wrong");
        setLoading(false);
      });
  }, [yearForRequest]);

  const safeSections = Array.isArray(sections) ? sections : [];

  function formatLabel(cls, section) {
    if (!cls) return "";

    const clsLower = cls.toLowerCase().trim();
    const clsNormalized = clsLower.replace(/\s+/g, "");

    if (clsLower.includes("prep")) {
      const parts = cls.split(" ");
      const roman = parts.length > 1 ? parts[1] : "";
      return `P ${roman} - ${section}`;
    }

    if (clsNormalized.includes("montcare")) {
      return `M - ${section}`;
    }

    return `${cls} - ${section}`;
  }

  // Sorting with numeric classes split 1-3 and 4-11 separately
  const sortedSections = [...safeSections].sort((a, b) => {
    const getRank = (cls) => {
      if (!cls) return 5;
      const c = cls.toLowerCase().trim();

      if (c.includes("montcare")) return 0;
      if (c.includes("prep")) return 1;

      const num = parseInt(c, 10);
      if (!isNaN(num)) {
        if (num >= 1 && num <= 3) return 2 + num / 100;
        if (num >= 4 && num <= 11) return 3 + num / 100;
      }

      return 4;
    };

    const rankA = getRank(a.class);
    const rankB = getRank(b.class);

    if (rankA < rankB) return -1;
    if (rankA > rankB) return 1;

    // If ranks equal, sort by section alphabetically
    if (a.section < b.section) return -1;
    if (a.section > b.section) return 1;
    return 0;
  });

  const data = {
    labels: sortedSections.map((item) => formatLabel(item.class, item.section)),
    datasets: [
      {
        label: "Present",
        data: sortedSections.map((item) => item.present || 0),
        backgroundColor: "#28a745",
      },
      {
        label: "Absent",
        data: sortedSections.map((item) => item.absent || 0),
        backgroundColor: "#dc3545",
      },
      {
        label: "Half Days",
        data: sortedSections.map((item) => item.halfdays || 0),
        backgroundColor: "#ffc107",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          font: { size: 10 },
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
      },
    },
  };

  if (loading) return <div>Loading attendance data...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="attendance-chart" style={{ height: "400px", width: "100%" }}>
      <Bar data={data} options={options} />
    </div>
  );
}

export default AttendanceChart;
